"use client"

import { useState, useEffect } from "react"
import { Link, Navigate } from "react-router-dom"
import { Line, Doughnut, Bar } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
} from "chart.js"
import { useAuth } from "../../../contexts/AuthContext"
import Swal from "sweetalert2"
import {
  ChartBarIcon,
  PaperAirplaneIcon,
  EnvelopeIcon,
  WalletIcon,
  ChartPieIcon,
  CheckCircleIcon,
  EyeIcon,
  XMarkIcon,
  ArrowUpIcon,
  CalendarIcon,
  ClockIcon,
  RocketLaunchIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  ArrowDownIcon,
} from "@heroicons/react/24/outline"

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement, BarElement)

function Home() {
  const [dashboardData, setDashboardData] = useState({
    stats: { balance: 0 },
    campaigns: [],
    reports: {
      today: {
        total: 0,
        sent: 0,
        delivered: 0,
        read: 0,
        failed: 0,
        pending: 0,
      },
      total: { total: 0 },
      yesterday: {
        total: 0,
        sent: 0,
        delivered: 0,
        read: 0,
        failed: 0,
        pending: 0,
      },
    },
    weeklyData: {
      labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      messages: [0, 0, 0, 0, 0, 0, 0],
      delivered: [0, 0, 0, 0, 0, 0, 0],
      read: [0, 0, 0, 0, 0, 0, 0],
      failed: [0, 0, 0, 0, 0, 0, 0],
      credits: [0, 0, 0, 0, 0, 0, 0],
    },
  })
  const [currentTime, setCurrentTime] = useState(new Date())
  const [loading, setLoading] = useState(true)
  const [syncingLimit, setSyncingLimit] = useState(false)
  const { user, getUserBalance, apiCall, fetchReportStats, syncUserLimit } = useAuth()

  const handleSyncHomeLimit = async () => {
    if (!user?._id) return
    try {
      setSyncingLimit(true)
      const res = await syncUserLimit(user._id)
      if (res && res.success) {
        Swal.fire({
          icon: "success",
          title: "Limit Synced",
          text: `Messaging limit synced successfully: ${res.wabaLimit || user?.wabaLimit || "10K"}`,
          timer: 1800,
          showConfirmButton: false,
          confirmButtonColor: "#4A7C59",
        })
      } else {
        Swal.fire({
          icon: "error",
          title: "Sync Failed",
          text: res?.error || res?.message || "Failed to sync limit from Meta API",
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (err) {
      console.error("Home sync limit error:", err)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to connect to Meta API",
        confirmButtonColor: "#4A7C59",
      })
    } finally {
      setSyncingLimit(false)
    }
  }

  useEffect(() => {
    loadDashboardData()
    const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000)
    const dataInterval = setInterval(loadDashboardData, 100000)
    return () => {
      clearInterval(timeInterval)
      clearInterval(dataInterval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (user?.role === "reseller") {
    return <Navigate to="/dashreseller" replace />
  }
  if (user?.role === "admin") {
    return <Navigate to="/dash-admin" replace />
  }
  if (user?.role === "superadmin") {
    return <Navigate to="/su-admin" replace />
  }

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const [statsData, campaignsData, todayReportsData, yesterdayReportsData, weeklyData] = await Promise.all([
        fetchDashboardStats(),
        fetchRecentCampaigns(),
        fetchReportStatsData(),
        fetchReportStatsData({
          startDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split("T")[0],
          endDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        }),
        fetchWeeklyData(),
      ])

      const totalMessagesData = await fetchReportStatsData({ allTime: true })

      setDashboardData({
        stats: statsData || { balance: 0 },
        campaigns: campaignsData || [],
        reports: {
          today: {
            total: todayReportsData?.total || 0,
            sent: todayReportsData?.sent || 0,
            delivered: todayReportsData?.delivered || 0,
            read: todayReportsData?.read || 0,
            failed: todayReportsData?.failed || 0,
            pending: todayReportsData?.pending || 0,
          },
          total: {
            total: totalMessagesData?.total || 0,
            sent: totalMessagesData?.sent || 0,
            delivered: totalMessagesData?.delivered || 0,
            read: totalMessagesData?.read || 0,
            failed: totalMessagesData?.failed || 0,
            pending: totalMessagesData?.pending || 0,
          },
          yesterday: {
            total: yesterdayReportsData?.total || 0,
            sent: yesterdayReportsData?.sent || 0,
            delivered: yesterdayReportsData?.delivered || 0,
            read: yesterdayReportsData?.read || 0,
            failed: yesterdayReportsData?.failed || 0,
            pending: yesterdayReportsData?.pending || 0,
          },
        },
        weeklyData: weeklyData || {
          labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
          messages: [0, 0, 0, 0, 0, 0, 0],
          delivered: [0, 0, 0, 0, 0, 0, 0],
          read: [0, 0, 0, 0, 0, 0, 0],
          failed: [0, 0, 0, 0, 0, 0, 0],
          credits: [0, 0, 0, 0, 0, 0, 0],
        },
      })
    } catch (e) {
      console.error("Error loading dashboard data:", e)
    } finally {
      setLoading(false)
    }
  }

  const fetchDashboardStats = async () => {
    try {
      const data = await getUserBalance()
      return data.success ? { balance: data.balance } : { balance: 0 }
    } catch (error) {
      console.error("Error fetching dashboard stats:", error)
      return { balance: 0 }
    }
  }

  const fetchRecentCampaigns = async () => {
    try {
      const data = await apiCall("/api/campaigns/user/campaigns?limit=5")
      return data?.campaigns || data || []
    } catch (error) {
      console.error("Error fetching campaigns:", error)
      return []
    }
  }

  const fetchReportStatsData = async (filters = {}) => {
    try {
      const { allTime, ...requestFilters } = filters

      if (!allTime && !requestFilters.startDate && !requestFilters.endDate) {
        const today = new Date().toISOString().split("T")[0]
        requestFilters.startDate = today
        requestFilters.endDate = today
      }

      const data = await fetchReportStats(requestFilters)
      return {
        total: data?.total || 0,
        sent: data?.sent || 0,
        delivered: data?.delivered || 0,
        read: data?.read || 0,
        failed: data?.failed || 0,
        pending: data?.pending || 0,
        totalSent: data?.totalSent || 0,
        creditsUsed: data?.creditsUsed || 0,
        totalCost: data?.totalCost || 0,
        campaigns: data?.campaigns || 0,
        performance: data?.performance || 0,
      }
    } catch (error) {
      console.error("Error fetching report stats:", error)
      return {
        total: 0,
        sent: 0,
        delivered: 0,
        read: 0,
        failed: 0,
        pending: 0,
        creditsUsed: 0,
        totalCost: 0,
        campaigns: 0,
        performance: 0,
      }
    }
  }

  const getDateKey = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  const fetchCreditUsageData = async (startDate, endDate) => {
    try {
      const params = new URLSearchParams({ startDate, endDate })
      const data = await apiCall(`/api/reports/credit-usage?${params.toString()}`)
      return data?.daily || []
    } catch (error) {
      console.error("Error fetching credit usage data:", error)
      return []
    }
  }

  const fetchWeeklyData = async () => {
    try {
      const weeklyPromises = []
      const today = new Date()
      const dates = []

      // Get data for last 7 days
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today)
        date.setDate(date.getDate() - i)
        const dateStr = getDateKey(date)

        dates.push({ date, dateStr })
        weeklyPromises.push(fetchReportStatsData({ startDate: dateStr, endDate: dateStr }))
      }

      const [weeklyResults, creditUsage] = await Promise.all([
        Promise.all(weeklyPromises),
        fetchCreditUsageData(dates[0].dateStr, dates[dates.length - 1].dateStr),
      ])
      const creditsByDate = creditUsage.reduce((acc, item) => {
        acc[item.date] = Number(item.creditsUsed || 0)
        return acc
      }, {})

      return {
        labels: dates.map(({ date }) => date.toLocaleDateString("en-US", { weekday: "short" })),
        messages: weeklyResults.map((day) => (day.sent || 0) + (day.delivered || 0) + (day.read || 0)),
        delivered: weeklyResults.map((day) => day.delivered || 0),
        read: weeklyResults.map((day) => day.read || 0),
        failed: weeklyResults.map((day) => day.failed || 0),
        credits: dates.map(({ dateStr }) => creditsByDate[dateStr] || 0),
      }
    } catch (error) {
      console.error("Error fetching weekly data:", error)
      return {
        labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
        messages: [0, 0, 0, 0, 0, 0, 0],
        delivered: [0, 0, 0, 0, 0, 0, 0],
        read: [0, 0, 0, 0, 0, 0, 0],
        failed: [0, 0, 0, 0, 0, 0, 0],
        credits: [0, 0, 0, 0, 0, 0, 0],
      }
    }
  }

  const refreshDashboard = () => loadDashboardData()

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const formatTimeAgo = (dateString) => {
    const now = new Date()
    const date = new Date(dateString)
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60))

    if (diffInHours < 1) return "Just now"
    if (diffInHours < 24) return `${diffInHours}h ago`
    return `${Math.floor(diffInHours / 24)}d ago`
  }

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "delivered":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
      case "process":
      case "processing":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
      case "failed":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
    }
  }

  const todayData = dashboardData.reports.today || {}
  const yesterdayData = dashboardData.reports.yesterday || {}
  const safeToday = {
    total: todayData.total || 0,
    sent: todayData.sent || 0,
    delivered: todayData.delivered || 0,
    read: todayData.read || 0,
    failed: todayData.failed || 0,
    pending: todayData.pending || 0,
  }
  const totalData = dashboardData.reports.total || {}
  const safeTotal = {
    total: totalData.total || 0,
    sent: totalData.sent || 0,
    delivered: totalData.delivered || 0,
    read: totalData.read || 0,
    failed: totalData.failed || 0,
    pending: totalData.pending || 0,
  }
  const attemptedToday = safeToday.sent + safeToday.delivered + safeToday.read + safeToday.failed
  const deliveredOrReadToday = safeToday.delivered + safeToday.read
  const successRateToday = attemptedToday > 0 ? Math.min(100, Math.round((deliveredOrReadToday / attemptedToday) * 100)) : 0
  const todayStatusTotal = safeToday.sent + safeToday.delivered + safeToday.read + safeToday.failed + safeToday.pending
  const statusDistributionSource = todayStatusTotal > 0 ? safeToday : safeTotal
  const statusDistributionValues = [
    statusDistributionSource.sent,
    statusDistributionSource.delivered,
    statusDistributionSource.read,
    statusDistributionSource.failed,
    statusDistributionSource.pending,
  ]
  const hasStatusDistributionData = statusDistributionValues.some((value) => value > 0)
  const getBoundedPercentage = (value, base) => {
    if (!base) return 0
    return Math.min(100, Math.round((Number(value || 0) / Number(base || 0)) * 100))
  }

  const calculatePercentageChange = (today, yesterday) => {
    if (yesterday === 0) return today > 0 ? 100 : 0
    return Math.round(((today - yesterday) / yesterday) * 100)
  }

  const todayPercentageChange = calculatePercentageChange(safeToday.total, yesterdayData.total || 0)
  const totalMessagesChange = calculatePercentageChange(
    dashboardData.reports.total.total,
    (dashboardData.reports.total.total || 0) * 0.88, // Approximate last month data
  )

  const messageChartData = {
    labels: dashboardData.weeklyData.labels || ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    datasets: [
      {
        label: "Messages Sent",
        data: dashboardData.weeklyData.messages,
        borderColor: "#2563eb",
        backgroundColor: "rgba(37, 99, 235, 0.12)",
        pointBackgroundColor: "#2563eb",
        borderWidth: 3,
        fill: false,
        tension: 0.35,
      },
      {
        label: "Messages Delivered",
        data: dashboardData.weeklyData.delivered,
        borderColor: "#10b981",
        backgroundColor: "rgba(16, 185, 129, 0.12)",
        pointBackgroundColor: "#10b981",
        borderWidth: 3,
        fill: false,
        tension: 0.35,
      },
      {
        label: "Messages Read",
        data: dashboardData.weeklyData.read,
        borderColor: "#f59e0b",
        backgroundColor: "rgba(245, 158, 11, 0.12)",
        pointBackgroundColor: "#f59e0b",
        borderWidth: 3,
        fill: false,
        tension: 0.35,
      },
      {
        label: "Messages Failed",
        data: dashboardData.weeklyData.failed,
        borderColor: "#ef4444",
        backgroundColor: "rgba(239, 68, 68, 0.12)",
        pointBackgroundColor: "#ef4444",
        borderWidth: 3,
        fill: false,
        tension: 0.35,
      },
    ],
  }

  const statusChartData = {
    labels: hasStatusDistributionData ? ["Sent", "Delivered", "Read", "Failed", "Pending"] : ["No data"],
    datasets: [
      {
        data: hasStatusDistributionData ? statusDistributionValues : [1],
        backgroundColor: hasStatusDistributionData
          ? ["#2563eb", "#10b981", "#f59e0b", "#ef4444", "#94a3b8"]
          : ["#e5e7eb"],
        borderWidth: 0,
      },
    ],
  }

  const creditChartData = {
    labels: dashboardData.weeklyData.labels || ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    datasets: [
      {
        label: "Credits Cut",
        data: dashboardData.weeklyData.credits,
        backgroundColor: "#f59e0b",
        borderRadius: 8,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
  }

  const creditChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        callbacks: {
          label: (context) => `Credits Cut: ${Number(context.raw || 0).toLocaleString()}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => Number(value).toLocaleString(),
        },
      },
    },
  }

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "62%",
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        enabled: hasStatusDistributionData,
      },
    },
  }

  const getCampaignMessageCounts = (campaign) => {
    const stats = campaign?.stats || {}
    const total = Number(stats.total || campaign?.phoneNumbers?.length || 0)
    const sent = Number(stats.sent || 0)
    const delivered = Number(stats.delivered || 0)
    const read = Number(stats.read || 0)
    const failed = Number(stats.failed || 0)
    const processedRaw = sent + delivered + read + failed
    const isFinished = ["completed", "failed"].includes(String(campaign?.status || "").toLowerCase())
    const processed = total > 0 && isFinished ? total : Math.min(processedRaw, total || processedRaw)

    return {
      total,
      sent,
      delivered,
      read,
      failed,
      sentLike: sent + delivered + read,
      processed,
      progressPercent: total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0,
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-yellow-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-yellow-50 dark:from-gray-900 dark:to-gray-800">
      <div className="mx-auto px-4 sm:px-4 lg:px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-green-700 to-yellow-600 bg-clip-text text-transparent mb-2">
                <ChartBarIcon className="w-8 h-8 inline-block mr-3 text-green-600" />
                Welcome {user?.username || ""}!
              </h1>
              <p className="text-gray-600 dark:text-gray-400 text-lg mb-4">
                Here's what's happening with your WhatsApp campaigns today
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                  Live Dashboard
                </span>
                <span className="text-gray-500 dark:text-gray-400 text-sm flex items-center">
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  {currentTime.toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
                <span className="text-gray-500 dark:text-gray-400 text-sm flex items-center">
                  <ClockIcon className="w-4 h-4 mr-1" />
                  {currentTime.toLocaleTimeString("en-US", { hour12: true })}
                </span>
              </div>
            </div>
            <div className="mt-4 lg:mt-0 flex gap-3">
              <Link to="/campaign">
                <button className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-600 to-yellow-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-yellow-700 transition-all duration-200 shadow-lg hover:shadow-xl">
                  <RocketLaunchIcon className="w-4 h-4 mr-2" />
                  Quick Campaign
                </button>
              </Link>
              <button
                onClick={refreshDashboard}
                className="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-semibold rounded-xl border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <ArrowPathIcon className="w-5 h-5 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Today's Messages */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {safeToday.total.toLocaleString()}
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm font-medium">Today's Messages</div>
                <div
                  className={`flex items-center mt-2 text-sm ${
                    todayPercentageChange >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                  }`}
                >
                  {todayPercentageChange >= 0 ? (
                    <ArrowUpIcon className="w-4 h-4 mr-1" />
                  ) : (
                    <ArrowDownIcon className="w-4 h-4 mr-1" />
                  )}
                  {Math.abs(todayPercentageChange)}% from yesterday
                </div>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <PaperAirplaneIcon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* Total Messages */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                   {(dashboardData.reports.total.total).toLocaleString()}
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm font-medium">Total Messages</div>
                <div
                  className={`flex items-center mt-2 text-sm ${
                    totalMessagesChange >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                  }`}
                >
                  <ChartBarIcon className="w-4 h-4 mr-1" />
                  {Math.abs(totalMessagesChange)}% this month
                </div>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                <EnvelopeIcon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* Wallet Balance */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  ₹{typeof dashboardData.stats.balance === "number" ? dashboardData.stats.balance.toFixed(2) : Number(dashboardData.stats.balance || 0).toFixed(2)}
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm font-medium">Wallet Balance</div>
                <div className="flex items-center mt-2 text-yellow-600 dark:text-yellow-400 text-sm">
                  {/* <CurrencyDollarIcon className="w-4 h-4 mr-1" /> */}
                  ₹{typeof dashboardData.stats.balance === "number" ? dashboardData.stats.balance.toFixed(2) : Number(dashboardData.stats.balance || 0).toFixed(2)} Credits
                </div>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg">
                <WalletIcon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* Success Rate */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {successRateToday}%
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm font-medium">Success Rate</div>
                <div
                  className={`flex items-center mt-2 text-sm ${
                    deliveredOrReadToday >= ((yesterdayData.delivered || 0) + (yesterdayData.read || 0))
                      ? "text-green-600 dark:text-green-400"
                      : "text-red-600 dark:text-red-400"
                  }`}
                >
                  <CheckCircleIcon className="w-4 h-4 mr-1" />
                  {deliveredOrReadToday} delivered/read today
                </div>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <ChartPieIcon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[
            { key: "sent", label: "Sent", color: "blue", icon: PaperAirplaneIcon },
            { key: "delivered", label: "Delivered", color: "green", icon: CheckCircleIcon },
            { key: "read", label: "Read", color: "yellow", icon: EyeIcon },
            { key: "failed", label: "Failed", color: "red", icon: XMarkIcon },
          ].map((metric) => {
            const value = safeToday[metric.key] || 0
            const percentage = getBoundedPercentage(value, attemptedToday)

            return (
              <div
                key={metric.key}
                className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 text-center"
              >
                <div className="mb-4">
                  <div
                    className={`w-16 h-16 bg-gradient-to-br from-${metric.color}-500 to-${metric.color}-600 rounded-2xl flex items-center justify-center mx-auto shadow-lg`}
                  >
                    <metric.icon className={`w-8 h-8 text-white ${metric.key === "read" ? "drop-shadow-sm" : ""}`} />
                  </div>
                </div>
                <div className={`text-2xl font-bold text-${metric.color}-600 dark:text-${metric.color}-400 mb-1`}>
                  {value.toLocaleString()}
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-2">{metric.label}</div>
                <div className={`text-${metric.color}-600 dark:text-${metric.color}-400 text-sm`}>
                  {percentage}% of attempted
                </div>
              </div>
            )
          })}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Message Analytics */}
          <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <ChartBarIcon className="w-5 h-5 mr-2 text-green-600" />
                  Message Analytics
                  <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1 animate-pulse"></div>
                    Live
                  </span>
                </h3>
                <button className="inline-flex items-center px-3 py-1 text-sm text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                  Last 7 Days
                  <ArrowDownIcon className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
            <div className="p-6 h-80">
              <Line data={messageChartData} options={chartOptions} />
            </div>
          </div>

          {/* WABA Health Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col justify-between p-6">
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">WABA Health</h3>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Phone status, quality & limits</p>
                </div>
                {/* <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSyncHomeLimit}
                    disabled={syncingLimit}
                    className="px-3 py-1.5 text-xs font-semibold text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors flex items-center gap-1.5"
                  >
                    <ArrowPathIcon className={`w-3.5 h-3.5 ${syncingLimit ? "animate-spin" : ""}`} />
                    {syncingLimit ? "Syncing..." : "Sync Limit"}
                  </button>
                  <Link
                    to="/waba-dashboard"
                    className="px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Manage
                  </Link>
                </div> */}
              </div>

              <div className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-4 mb-4">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-medium block mb-1">Phones</span>
                    <span className="text-xl font-bold text-gray-900 dark:text-white">
                      {user?.wabaNumbers?.length || (user?.phoneNumberId ? 1 : 0)}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-medium block mb-1">Verified Name</span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white truncate block max-w-[120px] mx-auto" title={user?.company || user?.name || user?.username}>
                      {user?.company || user?.name || user?.username || "Verified"}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-medium block mb-1">Messaging Limit</span>
                    <span className="text-xl font-bold text-blue-600 dark:text-blue-400 block">
                      {user?.wabaLimit || "Not set"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Pending: <strong className="text-gray-700 dark:text-gray-300">0</strong>
              </span>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">
                Quality: GREEN
              </span>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Credit Usage */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <CurrencyDollarIcon className="w-5 h-5 mr-2 text-yellow-600" />
                Credit Usage
              </h3>
            </div>
            <div className="p-6 h-72">
              <Bar data={creditChartData} options={creditChartOptions} />
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <ClockIcon className="w-5 h-5 mr-2 text-blue-600" />
                Recent Activity
              </h3>
            </div>
            <div className="p-6 h-72 overflow-y-auto">
              <div className="space-y-4">
                {dashboardData.campaigns.slice(0, 5).map((campaign, index) => {
                  const counts = getCampaignMessageCounts(campaign)
                  const status = String(campaign.status || "campaign").toLowerCase()
                  const activityTitle = status === "completed" ? "Campaign Completed" : "Campaign Updated"

                  return (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-yellow-500 rounded-xl flex items-center justify-center shadow-lg">
                        <PaperAirplaneIcon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 dark:text-white">{activityTitle}</div>
                        <div className="text-gray-600 dark:text-gray-400 text-sm">
                          {counts.processed}/{counts.total} messages processed for "{campaign.campaignName || "Unnamed Campaign"}"
                        </div>
                        <div className="text-gray-500 dark:text-gray-500 text-xs">
                          {formatTimeAgo(campaign.createdAt)}
                        </div>
                      </div>
                    </div>
                  )
                })}
                {dashboardData.campaigns.length === 0 && (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-8">No recent activity found</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Campaigns Table */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <ChartBarIcon className="w-5 h-5 mr-2 text-green-600" />
                Recent Campaigns
              </h3>
<Link to="/campaign-report">
              <button className="inline-flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50">
                View All Reports
                <ArrowUpIcon className="w-4 h-4 ml-1 rotate-45" />
              </button>
  </Link>
            </div>
          </div>
          <div className="overflow-x-auto">
            {dashboardData.campaigns.length > 0 ? (
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Campaign</th>
                    <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Status</th>
                    <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Messages</th>
                    {/* <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Success Rate</th> */}
                    {/* <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Credits Used</th> */}
                    <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Date</th>
                    {/* <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Actions</th> */}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {dashboardData.campaigns.map((campaign, index) => {
                    const counts = getCampaignMessageCounts(campaign)

                    return (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="py-4 px-6">
                          <div>
                            <div className="font-semibold text-gray-900 dark:text-white">
                              {campaign.campaignName || "Unnamed Campaign"}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {campaign.templateName || "No template"}
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-6 whitespace-nowrap">
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusBadge(campaign.status)}`}
                          >
                            {campaign.status || "Unknown"}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3 min-w-36">
                            <span className="font-semibold text-gray-900 dark:text-white tabular-nums min-w-16">
                              {counts.processed}/{counts.total}
                            </span>
                            <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                              <div
                                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${counts.progressPercent}%` }}
                              ></div>
                            </div>
                          </div>
                        </td>
                        {/* <td className="py-4 px-6">
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-semibold ${
                              successRate >= 80
                                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                : successRate >= 60
                                  ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                                  : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                            }`}
                          >
                            {successRate}%
                          </span>
                        </td> */}
                        {/* <td className="py-4 px-6">
                          <span className="flex items-center text-gray-900 dark:text-white">
                            <CurrencyDollarIcon className="w-4 h-4 mr-1" />
                            {stats.creditsUsed || stats.sent || 0}
                          </span>
                        </td> */}
                        <td className="py-4 px-6">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(campaign.createdAt)}
                          </span>
                        </td>
                        {/* <td className="py-4 px-6">
                          <div className="flex space-x-2">
                            <button className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors">
                              <EyeIcon className="w-4 h-4 drop-shadow-sm" />
                            </button>
                            <button className="p-2 text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors">
                              <ArrowDownIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td> */}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-yellow-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <ChartBarIcon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Campaigns Found</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  You don't have any campaigns yet. Create your first campaign to get started.
                </p>
                <button className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-yellow-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-yellow-700 transition-all duration-200 shadow-lg">
                  <RocketLaunchIcon className="w-5 h-5 mr-2" />
                  Create Campaign
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Home
